import * as arrow from "apache-arrow";
import React from "react";
import { Box } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { DatePivotParams } from "../../entities/date_pivot_params";
import { MUIFlatTableColumn } from "../../components/universal_mui_table/MUIFlatTable";
import dayjs from "../../utils/dayjs";

const FIXED_COLUMNS = new Set([
  "segmentation",
  "segment",
  "segmentation_key",
  "measure",
  "measure_rank",
  "segmentation_rank",
  "from_location",
  "to_location",
  "TOTAL",
]);

interface DetailRow {
  fromLocation: string;
  toLocation: string;
  dateValues: Record<string, number>;
  total: number;
}

interface GroupInfo {
  groupKey: string;
  detailRows: DetailRow[];
  aggregatedDateValues: Record<string, number>;
  aggregatedTotal: number;
  startRowIndex: number;
}

export const createTransferRecommendationsMatrix = (args: {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
  viewParams: DatePivotParams;
  demandWalkConfig?: any;
  purchaseOrderConfig?: any;
  sortConfig: Array<{
    key: string;
    direction: "asc" | "desc";
    index: number;
  }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  onSort: (key: string, e: React.MouseEvent<HTMLTableCellElement>) => void;
  columnSchema: Record<
    string,
    {
      dtype: string;
      min?: string;
      max?: string;
      unique_values?: string[];
    }
  >;
  theme: Theme;
  columnHeaderMap?: Record<string, string>;
  firstMount?: React.MutableRefObject<boolean>;
}) => {
  const {
    arrowTable,
    columns,
    viewParams,
    sortConfig,
    setSortConfig,
    onSort,
    theme,
    firstMount,
  } = args;

  if (!arrowTable || !columns) return null;

  // Determine group fields from all selected segments
  const groupFields =
    viewParams.segments && viewParams.segments.length > 0
      ? viewParams.segments
      : ["sku"];
  const groupLabel = groupFields
    .map(
      (f) =>
        args.columnHeaderMap?.[f] ??
        f.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    )
    .join(" | ");

  const arrowColumnNames = columns.map((c) => c.name);

  // Identify date columns (numeric timestamp names)
  const dateColumnNames = columns
    .filter((c) => {
      const n = Number(c.name);
      return !isNaN(n) && !FIXED_COLUMNS.has(c.name) && n > 1_000_000_000_000;
    })
    .map((c) => c.name)
    .sort((a, b) => Number(a) - Number(b));

  const hasTotalColumn = columns.some((c) => c.name === "TOTAL");

  // Get vectors
  const groupVectors = groupFields.map((f) => arrowTable.getChild(f));
  const fromLocVector = arrowTable.getChild("from_location");
  const toLocVector = arrowTable.getChild("to_location");
  const totalVector = arrowTable.getChild("TOTAL");
  const measureVector = arrowTable.getChild("measure");
  const measureRankVector = arrowTable.getChild("measure_rank");

  const missingFields = groupFields.filter((_, i) => !groupVectors[i]);
  if (missingFields.length === groupFields.length || !fromLocVector || !toLocVector) {
    return null;
  }

  // Date vectors
  const dateVectors = new Map<string, arrow.Vector>();
  for (const name of dateColumnNames) {
    const vec = arrowTable.getChild(name);
    if (vec) dateVectors.set(name, vec);
  }

  // Filter rows by measure when the matrix has multiple measures per segmentation.
  // Use the selected measure from viewParams, or fall back to measure_rank 0.
  const rowIndices: number[] = [];
  if (measureVector) {
    if (viewParams.measure) {
      const target = viewParams.measure.toLowerCase();
      for (let i = 0; i < arrowTable.numRows; i++) {
        if (String(measureVector.get(i) ?? "").toLowerCase() === target) {
          rowIndices.push(i);
        }
      }
    }
    if (rowIndices.length === 0) {
      // No measure matched or none selected — use measure_rank 0
      for (let i = 0; i < arrowTable.numRows; i++) {
        const rank = measureRankVector ? Number(measureRankVector.get(i)) : 0;
        if (rank === 0) rowIndices.push(i);
      }
    }
  } else {
    for (let i = 0; i < arrowTable.numRows; i++) rowIndices.push(i);
  }

  // Group by all selected segment fields, joined with ' | '
  const groupMap = new Map<string, DetailRow[]>();
  for (const i of rowIndices) {
    const groupVal = groupVectors
      .map((v) => String(v?.get(i) ?? ""))
      .join(" | ");
    const fromLoc = String(fromLocVector.get(i) ?? "");
    const toLoc = String(toLocVector.get(i) ?? "");

    const dateValues: Record<string, number> = {};
    for (const [name, vec] of dateVectors) {
      const v = vec.get(i);
      dateValues[name] =
        typeof v === "number" ? v : Number(v) || 0;
    }

    const rawTotal = totalVector?.get(i);
    const total =
      typeof rawTotal === "number" ? rawTotal : Number(rawTotal) || 0;

    if (!groupMap.has(groupVal)) groupMap.set(groupVal, []);
    groupMap.get(groupVal)!.push({
      fromLocation: fromLoc,
      toLocation: toLoc,
      dateValues,
      total,
    });
  }

  // Build GroupInfo array with aggregation
  const groupInfos: GroupInfo[] = [];
  for (const [groupVal, details] of groupMap) {
    const aggregatedDateValues: Record<string, number> = {};
    for (const name of dateColumnNames) {
      aggregatedDateValues[name] = details.reduce(
        (sum, d) => sum + (d.dateValues[name] || 0),
        0
      );
    }
    const aggregatedTotal = details.reduce((sum, d) => sum + d.total, 0);

    groupInfos.push({
      groupKey: groupVal,
      detailRows: details,
      aggregatedDateValues,
      aggregatedTotal,
      startRowIndex: 0,
    });
  }


  // Sort groups
  groupInfos.sort((a, b) => {
    for (const sort of sortConfig) {
      let aVal: number | string;
      let bVal: number | string;

      if (sort.key === "TOTAL") {
        aVal = a.aggregatedTotal;
        bVal = b.aggregatedTotal;
      } else if (sort.key === "segmentation") {
        aVal = a.groupKey;
        bVal = b.groupKey;
      } else {
        aVal = a.aggregatedDateValues[sort.key] ?? 0;
        bVal = b.aggregatedDateValues[sort.key] ?? 0;
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        const cmp = aVal - bVal;
        if (cmp !== 0) return sort.direction === "asc" ? cmp : -cmp;
      } else {
        const cmp = String(aVal).localeCompare(String(bVal));
        if (cmp !== 0) return sort.direction === "asc" ? cmp : -cmp;
      }
    }
    return 0;
  });

  // Set initial sort on first mount — return early so the state update
  // triggers a re-render with the correct sort applied.
  if (firstMount?.current) {
    firstMount.current = false;
    setSortConfig([{ key: "TOTAL", direction: "desc", index: 0 }]);
    return {
      columns: [],
      getRow: () => ({ type: "detail", dateValues: {}, segmentationRank: 0 }),
      rowCount: 0,
    };
  }

  // Compute virtual row positions — all groups always expanded
  let totalRowCount = 0;
  for (const info of groupInfos) {
    info.startRowIndex = totalRowCount;
    totalRowCount += 1 + info.detailRows.length;
  }

  // Build date display info
  const allDates = dateColumnNames
    .map((k) => ({ key: k, date: dayjs.utc(Number(k)) }))
    .filter((d) => d.date.isValid());

  const formatValue = (value: unknown): string => {
    if (value == null || value === "") return "";
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  // Build columns
  const muiColumns: MUIFlatTableColumn[] = [
    {
      key: "segmentation",
      label: groupLabel,
      width: 180,
      align: "left",
      sortable: true,
      sticky: true,
      sortDirection: sortConfig.find((s) => s.key === "segmentation")?.direction,
      onSort: onSort
        ? (e: React.MouseEvent<HTMLTableCellElement>) => onSort("segmentation", e)
        : undefined,
      headerType: new arrow.Utf8(),
      renderCell: (row: any) => (
        <Box
          sx={{
            p: 1,
            height: "100%",
            display: "flex",
            alignItems: "center",
            fontWeight: row.type === "group" ? "bold" : "normal",
            backgroundColor:
              row.type === "detail"
                ? theme.palette.action.hover
                : undefined,
          }}
        >
          {row.type === "group" ? row.segmentation : ""}
        </Box>
      ),
    },
    {
      key: "from_location",
      label: "From",
      width: 150,
      align: "left",
      sortable: false,
      sticky: true,
      headerType: new arrow.Utf8(),
      renderCell: (row: any) => (
        <Box
          sx={{
            p: 1,
            height: "100%",
            display: "flex",
            alignItems: "center",
            backgroundColor:
              row.type === "detail"
                ? theme.palette.action.hover
                : undefined,
          }}
        >
          {row.from_location ?? ""}
        </Box>
      ),
    },
    {
      key: "to_location",
      label: "To",
      width: 150,
      align: "left",
      sortable: false,
      sticky: true,
      headerType: new arrow.Utf8(),
      renderCell: (row: any) => (
        <Box
          sx={{
            p: 1,
            height: "100%",
            display: "flex",
            alignItems: "center",
            backgroundColor:
              row.type === "detail"
                ? theme.palette.action.hover
                : undefined,
          }}
        >
          {row.to_location ?? ""}
        </Box>
      ),
    },
    ...allDates.map(({ key }) => ({
      key,
      width: 100,
      sortable: true,
      sortDirection: sortConfig.find((s) => s.key === key)?.direction,
      onSort: onSort
        ? (e: React.MouseEvent<HTMLTableCellElement>) => onSort(key, e)
        : undefined,
      headerType: new arrow.Timestamp(arrow.TimeUnit.MILLISECOND),
      renderCell: (row: any) => {
        const value = row[key];
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor:
                row.type === "detail"
                  ? theme.palette.action.hover
                  : undefined,
            }}
          >
            {formatValue(value)}
          </Box>
        );
      },
    })),
  ];

  if (hasTotalColumn) {
    muiColumns.push({
      key: "TOTAL",
      label: "Total",
      width: 100,
      sortable: true,
      sortDirection: sortConfig.find((s) => s.key === "TOTAL")?.direction,
      onSort: onSort
        ? (e: React.MouseEvent<HTMLTableCellElement>) => onSort("TOTAL", e)
        : undefined,
      renderCell: (row: any) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: row.type === "group" ? "bold" : "normal",
            backgroundColor:
              row.type === "detail"
                ? theme.palette.action.hover
                : undefined,
          }}
        >
          {formatValue(row.TOTAL)}
        </Box>
      ),
    });
  }

  // getRow: virtual index → group header or detail row
  const getRow = (rowIndex: number): Record<string, any> => {
    if (rowIndex < 0 || rowIndex >= totalRowCount) return {};

    for (const info of groupInfos) {
      const groupEnd = info.startRowIndex + 1 + info.detailRows.length;

      if (rowIndex >= info.startRowIndex && rowIndex < groupEnd) {
        const localIdx = rowIndex - info.startRowIndex;

        if (localIdx === 0) {
          // Group header row — segment label only, no data
          return {
            type: "group",
            groupKey: info.groupKey,
            segmentation: info.groupKey,
            from_location: "",
            to_location: "",
          };
        }

        // Detail row (individual from → to pair)
        const detail = info.detailRows[localIdx - 1];
        if (!detail) return {};

        const row: Record<string, any> = {
          type: "detail",
          groupKey: info.groupKey,
          segmentation: "",
          from_location: detail.fromLocation,
          to_location: detail.toLocation,
          TOTAL: detail.total,
        };
        for (const name of dateColumnNames) {
          row[name] = detail.dateValues[name] ?? 0;
        }
        return row;
      }
    }
    return {};
  };

  return {
    columns: muiColumns,
    getRow,
    rowCount: totalRowCount,
  };
};
